import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button, Result, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import * as PropTypes from 'prop-types'
import LoginError from './LoginError'
import { useMutation } from '@apollo/client'
import { handlePostLogout } from '../../shared/utils/helpers'
import { LoadingOutlined, WifiOutlined } from '@ant-design/icons'
import { LogoutIcon } from '../../shared/utils/icons'
import { LOGOUT } from '@dentolize/clinic-mobile/src/shared/store/mutations/authMutations'

const Error = props => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  const { t, i18n } = useTranslation()
  const { search: locationSearch } = useLocation()

  const { error, errorStatus, errorTitle, errorSubTitle, to, errorBtn, resetTable } = props

  const [logout, { loading }] = useMutation(LOGOUT)

  useEffect(() => {
    const onlineHandler = () => setIsOnline(true)
    const offlineHandler = () => setIsOnline(false)

    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)

    return () => {
      window.removeEventListener('online', onlineHandler)
      window.removeEventListener('offline', offlineHandler)
    }
  }, [])

  const handleLogout = async () => {
    await logout()
    handlePostLogout()
  }

  let status = '404'
  let title = errorTitle || '404'
  let subTitle = !errorSubTitle || errorSubTitle === ' ' ? t('app.notFoundSub') : errorSubTitle || t('common.somethingWrong')
  let extra = (
    <Space>
      <Link to={to || '/'}>
        <Button type="primary">{errorBtn || t('common.backHome')}</Button>
      </Link>
      {resetTable && locationSearch ? (
        <Button type="primary" onClick={resetTable} danger>
          {t('app.clear')}
        </Button>
      ) : null}
    </Space>
  )

  if (error?.message?.includes('Not Logged in')) {
    return <LoginError />
  } else if (errorStatus === 'server') {
    status = '500'
    title = t('common.makingUpdates')
    subTitle = t('common.makingUpdatesSub')
    extra = [
      <Button key="refresh" onClick={() => window.location.reload()} type="primary">
        {t('common.refresh')}
      </Button>,
      <a key="status" href="https://dentolize.com/status/" target="_blank" rel="noopener noreferrer">
        <Button key="status" type="primary">
          {t('app.systemStatus')}
        </Button>
      </a>
    ]
  } else if (errorStatus === 'noBranch') {
    status = 'error'
    title = t('common.noBranch')
    subTitle = t('common.noBranchSub')
    extra = (
      <Space wrap>
        <Button
          type="primary"
          danger
          loading={loading}
          onClick={handleLogout}
          icon={loading ? <LoadingOutlined /> : <LogoutIcon rotate={i18n.language === 'ar' ? 0 : 180} />}
        >
          {t('home.nav.logout')}
        </Button>
      </Space>
    )
  } else if (error?.message?.includes('Network error')) {
    status = '500'
    title = t('common.noInternet')
    subTitle = t('common.noInternetSub')
    extra = (
      <Space>
        <Link to={props.to || '/'}>
          <Button type="primary">{t('common.backHome')}</Button>
        </Link>
        {resetTable && locationSearch ? (
          <Button type="primary" onClick={resetTable} danger>
            {t('app.clear')}
          </Button>
        ) : null}
      </Space>
    )
  }

  return (
    <div>
      <Result
        status={isOnline ? status : 'error'}
        title={isOnline ? title : t('common.noInternet')}
        subTitle={isOnline ? subTitle : t('common.noInternetSub')}
        extra={isOnline ? extra : undefined}
        icon={isOnline ? undefined : <WifiOutlined />}
      />
    </div>
  )
}

Error.propTypes = {
  error: PropTypes.object,
  to: PropTypes.string,
  button: PropTypes.string,
  errorStatus: PropTypes.string,
  errorTitle: PropTypes.string,
  errorSubTitle: PropTypes.string,
  errorBtn: PropTypes.string,
  resetTable: PropTypes.func
}

export default Error
